import React, { useEffect, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { MenuItem, TextField } from '@mui/material';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const STATUS_OPTIONS = ['Active', 'Deactive'];

const initialForm = {
  site_name: '',
  site_address: '',
  status: 'Active',
};

const normalizeStatusForForm = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'inactive' || normalized === 'deactive') return 'Deactive';
  return 'Active';
};

const AddSiteModal = ({ isOpen, onClose, onSuccess, mode = 'create', site = null }) => {
  const { mode: themeMode } = useThemeMode();
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(false);
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && site) {
        setForm({
          site_name: site.site_name || '',
          site_address: site.site_address || '',
          status: normalizeStatusForForm(site.status),
        });
      } else {
        setForm(initialForm);
      }
      setError('');
      setShowFormLoading(false);
      setShowValidation(false);
    }
  }, [isOpen, mode, site]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (error) setError('');
  };

  const isFormValid = form.site_name.trim() && form.site_address.trim() && form.status;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowValidation(true);

    if (!isFormValid) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setShowFormLoading(true);
    setError('');

    try {
      const payload = {
        site_name: form.site_name.trim(),
        site_address: form.site_address.trim(),
        status: form.status,
      };

      const isEditMode = mode === 'edit' && site?.id;
      const response = isEditMode
        ? await dataAPI.updateSite(site.id, payload, { showLoading: false })
        : await dataAPI.createSite(payload, { showLoading: false });

      if (response?.status === 'success') {
        onSuccess(response.data, isEditMode);
        onClose();
      } else {
        setError(response?.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} site.`);
      }
    } catch (apiError) {
      setError(
        apiError?.message ||
          `An error occurred while ${mode === 'edit' ? 'updating' : 'creating'} the site.`
      );
    } finally {
      setIsLoading(false);
      setShowFormLoading(false);
    }
  };

  if (!isOpen) return null;

  const isEditMode = mode === 'edit';
  const modalTitle = isEditMode ? 'Edit Site' : 'Add Site';
  const submitLabel = isEditMode ? 'Save Changes' : 'Add Site';

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: themeMode === 'dark' ? '#444' : '#ddd',
      },
      '&:hover fieldset': {
        borderColor: '#26C3C0',
        boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#26C3C0',
        boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
      },
      '&.Mui-error fieldset': {
        borderColor: '#f44336',
      },
    },
    '& .MuiInputLabel-root': {
      color: themeMode === 'dark' ? '#999' : '#666',
      fontSize: '16px',
    },
    '& .MuiFormHelperText-root': {
      color: '#f44336',
      fontSize: '14px',
    },
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: themeMode === 'dark' ? '#1A1A1A' : '#ffffff',
          borderRadius: '20px',
          padding: '40px',
          width: '520px',
          maxWidth: '90vw',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
          }}
        >
          <h2
            style={{
              color: themeMode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '28px',
              margin: 0,
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
          >
            {modalTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: themeMode === 'dark' ? '#FFFFFF' : '#666666',
              fontSize: '24px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {showFormLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: themeMode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px',
            }}
          >
            <div className="fill-animation-container">
              <div className="fill-animation-background" />
              <div className="fill-animation-progress" />
              <img src={AppIconBlue} alt="Loading" className="fill-animation-icon pulse-animation-icon" />
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            size="small"
            required
            fullWidth
            label="Site Name"
            value={form.site_name}
            onChange={handleChange('site_name')}
            disabled={isLoading}
            error={showValidation && !form.site_name.trim()}
            helperText={showValidation && !form.site_name.trim() ? 'Site name is required' : ''}
            sx={{ ...textFieldSx, mb: 2 }}
          />

          <TextField
            size="small"
            required
            fullWidth
            label="Site Address"
            value={form.site_address}
            onChange={handleChange('site_address')}
            disabled={isLoading}
            error={showValidation && !form.site_address.trim()}
            helperText={showValidation && !form.site_address.trim() ? 'Site address is required' : ''}
            sx={{ ...textFieldSx, mb: 2 }}
          />

          <TextField
            size="small"
            required
            fullWidth
            select
            label="Status"
            value={form.status}
            onChange={handleChange('status')}
            disabled={isLoading}
            sx={textFieldSx}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          {error && (
            <div
              style={{
                color: '#ff6b6b',
                fontSize: '14px',
                marginTop: '12px',
                fontFamily: 'Urbanist, Arial, sans-serif',
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !isFormValid}
            style={{
              width: '100%',
              height: '56px',
              marginTop: '24px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor:
                isLoading || !isFormValid
                  ? themeMode === 'dark'
                    ? '#666666'
                    : '#cccccc'
                  : '#26C3C0',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'Urbanist, Arial, sans-serif',
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Saving...' : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddSiteModal;
